import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { TableCell } from '@material-ui/core';

import { localToUtcTime } from 'customer/components/policy/dates';
import { SelectableRow } from 'core/components/table/selectable-row';
import { SelectableCell } from 'core/components/table/selectable-cell';
import { AuthContext } from 'core/components/auth';
import { timeFormatter, awsDateToDateFormatter } from 'core/helpers/formatters';
import useStyles from './offer-results.styles';

const deriveAddress = (address) => {
  const parts = [];
  if (address.address) {
    parts.push(address.address);
  }
  if (address.city) {
    parts.push(address.city);
  }
  if (address.state) {
    parts.push(address.state);
  }
  if (address.zip) {
    parts.push(address.zip);
  }
  return parts.join(', ').replace(/, ([^,]+)$/, ' $1'); // Last part without comma
};

const POLICY_TYPE_MAPPINGS = {
  H: 'Home',
  R: 'Renters',
  C: 'Condo',
  A: 'Auto',
  HA: ['Home', 'Auto'],
  AR: ['Auto', 'Renters'],
  CA: ['Condo', 'Auto']
};

const deriveEligibleOffers = (options) => {
  const eligibleOffers = new Set();
  options.forEach((option) => {
    const types = POLICY_TYPE_MAPPINGS[option.type];
    if (Array.isArray(types)) {
      types.forEach((t) => eligibleOffers.add(t));
    } else if (types) {
      eligibleOffers.add(types);
    }
  });

  // Format the output
  if (eligibleOffers.has('Home') && eligibleOffers.has('Auto')) {
    return 'Home & Auto';
  }
  if (eligibleOffers.has('Condo') && eligibleOffers.has('Auto')) {
    return 'Condo & Auto';
  }
  if (eligibleOffers.has('Renters') && eligibleOffers.has('Auto')) {
    return 'Renters & Auto';
  }

  const single = ['Home', 'Auto', 'Renters', 'Condo'].find((type) => eligibleOffers.has(type));
  return single ?? 'N/A';
};

// Cell component to conditionally render TableCell or SelectableCell based on permissions
const Cell = ({ children, hasPermission, offerLink, classes }) => {
  if (hasPermission) {
    return (
      <SelectableCell to={offerLink} classes={{ root: classes.cell }}>
        {children}
      </SelectableCell>
    );
  }
  return <TableCell classes={{ root: classes.cell }}>{children}</TableCell>;
};

Cell.propTypes = {
  children: PropTypes.node.isRequired,
  hasPermission: PropTypes.bool.isRequired,
  offerLink: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired
};

const OfferItem = ({ offer }) => {
  const classes = useStyles();
  const history = useHistory();
  const { canQuote, viewOnly } = useContext(AuthContext);

  const date = localToUtcTime(offer.updatedDateTime, offer.quote.submittedAddress.state);

  const onOfferEdit = useCallback(() => {
    const { id } = offer;
    if (canQuote || viewOnly) {
      history.push(`/offer/${id}`);
    }
  }, [offer, canQuote, viewOnly, history]);

  const offerLink = `/offer/${offer.id}`;
  const hasPermission = canQuote || viewOnly;

  return (
    <SelectableRow hover id={offer.id} onClick={onOfferEdit} data-cy="row">
      <Cell hasPermission={hasPermission} offerLink={offerLink} classes={classes}>
        {offer.quote.fname}
      </Cell>
      <Cell hasPermission={hasPermission} offerLink={offerLink} classes={classes}>
        {offer.quote.lname}
      </Cell>
      <Cell hasPermission={hasPermission} offerLink={offerLink} classes={classes}>
        {deriveAddress(offer.quote.submittedAddress)}
      </Cell>
      <Cell hasPermission={hasPermission} offerLink={offerLink} classes={classes}>
        {deriveEligibleOffers(offer.options)}
      </Cell>
      <Cell hasPermission={hasPermission} offerLink={offerLink} classes={classes}>
        {offer.quote.rep || ''}
      </Cell>
      <Cell hasPermission={hasPermission} offerLink={offerLink} classes={classes}>
        {offer.updatedDateTime && (
          <div>
            {`${awsDateToDateFormatter(date)} `}
            {timeFormatter(date)}
          </div>
        )}
      </Cell>
    </SelectableRow>
  );
};

OfferItem.propTypes = {
  offer: PropTypes.object.isRequired
};

export default OfferItem;
